import { IconPencil, IconPlus } from '@tabler/icons-react'
import { ReactNode } from 'react'
import { twMerge } from 'tailwind-merge'

import { AstraButton, AstraIcon, AstraParagraph, AstraTooltip, astraTwMerge, ColumnSize, DataTable, RowType } from '~/astra'
import { DipsResource, type HospitalConfigLocation } from '~/storm'
import { getDayOvernightByLocationCareLevel } from '~/utils/dayOvernight'

import { useResourceNavigation } from '../useResourceNavigation'

type Column<T extends string = string> = ColumnSize & {
    id: T
    label: string
    tooltip?: string
}

const TextCell = ({ children }: { children: ReactNode }) => (
    <AstraParagraph variant="body" className="truncate whitespace-normal text-default">
        {children}
    </AstraParagraph>
)

const leftPadding = 'pl-6'
const bodyCellClassName = twMerge('flex h-full w-full !cursor-default !select-text items-center border-none', leftPadding)

const getHeaders = <T extends Column>(_cols: T[]): RowType<T>[] => [
    {
        key: 'header',
        cellClassName: () => twMerge('flex w-full bg-surface-strong p-3', leftPadding),
        cellRender: ({ tooltip, label }) => (
            <AstraTooltip tooltipContent={tooltip} hidden={!tooltip}>
                <AstraParagraph weight="medium" variant="body-small" className="text-default">
                    {label}
                </AstraParagraph>
            </AstraTooltip>
        ),
    },
]

const locationCols = [
    { id: 'roomCode', label: 'Stue' },
    { id: 'careLevel', label: 'Standard omsorgsnivå', tooltip: 'I taktisk plan antas det at alle operasjonene på stuen har dette omsorgsnivået' },
    { id: 'action', label: '' },
] as const satisfies Column[]
type LocationColumn = (typeof locationCols)[number]

const LocationCell = ({
    column,
    location,
    onEdit,
}: {
    column: LocationColumn
    location: HospitalConfigLocation
    onEdit: (location: HospitalConfigLocation) => void
}) => {
    if (column.id === 'roomCode') {
        return <TextCell>{location.room_code}</TextCell>
    }
    if (column.id === 'careLevel') {
        const careLevelLabel = getDayOvernightByLocationCareLevel(location.default_care_level).label
        return <TextCell>{careLevelLabel}</TextCell>
    }
    column.id satisfies 'action'
    return <AstraButton variant="ghost" size="medium" label="Rediger" icon={<AstraIcon icon={IconPencil} size="small" />} onClick={() => onEdit(location)} />
}

const EmptyTable = ({ message = 'Fant ingen rom', headerColumns }: { message?: string; headerColumns: Column[] }) => (
    <DataTable
        cols={headerColumns}
        rows={[{ id: 'empty' }]}
        fullWidthCells
        headers={getHeaders(headerColumns)}
        className="border-spacing-0"
        optimizeRendering={{ cellHeight: 'tall' }}
        body={{
            cellClassName: (_, col) =>
                astraTwMerge(bodyCellClassName, col.id === headerColumns[0]?.id ? 'col-span-full flex items-center justify-center border-none' : 'hidden'),
            cellRender: (_, column) =>
                column.id === headerColumns[0]?.id ? (
                    <AstraParagraph variant="body" className="text-default">
                        {message}
                    </AstraParagraph>
                ) : null,
        }}
    />
)

export const LocationsTable = ({ locations }: { locations: HospitalConfigLocation[] }) => {
    const { navigateToEdit } = useResourceNavigation()

    if (locations.length === 0) {
        return <EmptyTable headerColumns={locationCols} />
    }
    return (
        <DataTable
            cols={locationCols}
            rows={locations}
            fullWidthCells
            headers={getHeaders(locationCols)}
            optimizeRendering={{ cellHeight: 'tall' }}
            className="border-spacing-0"
            body={{
                cellClassName: () => bodyCellClassName,
                cellRender: (location, column) => <LocationCell column={column} location={location} onEdit={l => navigateToEdit(l.id)} />,
            }}
        />
    )
}

const dipsCols = [
    { id: 'roomCode', label: 'Stue' },
    { id: 'name', label: 'Navn' },
    { id: 'action', label: '' },
] as const satisfies Column[]
type DipsColKey = (typeof dipsCols)[number]['id']
type DipsRoomColumn = (typeof dipsCols)[number]

type DipsRoomWithLocation = DipsResource & { location: HospitalConfigLocation | null }

const DipsRoomCell = ({
    column,
    resource,
    onAdd,
}: {
    column: DipsRoomColumn
    resource: DipsRoomWithLocation
    onAdd: (resource: DipsRoomWithLocation) => void
}) => {
    const cell: Record<DipsColKey, ReactNode> = {
        roomCode: <TextCell>{resource.shortName ?? '–'}</TextCell>,
        name: <TextCell>{resource.name ?? '–'}</TextCell>,
        action: resource.location ? null : (
            <AstraButton
                variant="primary"
                size="medium"
                label="Legg til Hero"
                icon={<AstraIcon icon={IconPlus} size="small" />}
                onClick={() => onAdd(resource)}
            />
        ),
    }

    return cell[column.id]
}

export const DipsRoomsTable = ({ resources }: { resources: DipsRoomWithLocation[] }) => {
    const { navigateToNew } = useResourceNavigation()

    if (resources.length === 0) {
        return <EmptyTable message="Fant ingen stuer med oppdateringer fra DIPS" headerColumns={dipsCols} />
    }
    return (
        <DataTable
            cols={dipsCols}
            rows={resources}
            fullWidthCells
            headers={getHeaders(dipsCols)}
            optimizeRendering={{ cellHeight: 'tall' }}
            className="border-spacing-0"
            body={{
                cellClassName: () => bodyCellClassName,
                cellRender: (resource, column) => <DipsRoomCell column={column} resource={resource} onAdd={r => navigateToNew(r.id)} />,
            }}
        />
    )
}
